import HelpOutlineIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { type ReactNode, Suspense, useState } from 'react';
import { Link as RouterLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { BrandLogo, DarkModeMenuItem, LoadingState } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { DevModeBanner } from '@/features/payments/DevModeBanner';
import { TrialBanner } from '@/features/trials/TrialBanner';
import { InstallBanner } from '@/features/install/InstallBanner';
import { type Area, adminNav, areaFromPath, areaMeta, isActive, learnerBottomNav, learnerNav, providerNav } from './navigation';

const DRAWER_WIDTH = 264;

export function Logo({ to = '/', inverted = false }: { to?: string; inverted?: boolean }) {
  return (
    <Box component={RouterLink} to={to} sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }} aria-label="TradePrep home">
      <BrandLogo height={30} onDark={inverted} />
    </Box>
  );
}

function DrawerContent({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const area = areaFromPath(pathname);
  const items = area === 'provider' ? providerNav : area === 'admin' ? adminNav : learnerNav;
  const areas: Area[] = ['learner', ...(hasRole('content_provider') ? (['provider'] as Area[]) : []), ...(hasRole('admin') ? (['admin'] as Area[]) : [])];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Toolbar sx={{ px: 2.5 }}>
        <Logo to={user ? '/dashboard' : '/'} />
      </Toolbar>
      {user && areas.length > 1 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <ToggleButtonGroup
            exclusive
            fullWidth
            size="small"
            value={area}
            onChange={(_, v: Area | null) => {
              if (v) {
                navigate(areaMeta[v].home);
                onNavigate?.();
              }
            }}
            aria-label="Switch area"
          >
            {areas.map((a) => (
              <ToggleButton key={a} value={a} sx={{ gap: 0.5 }}>
                {areaMeta[a].label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      <List component="nav" sx={{ px: 1.5, flex: 1 }} aria-label="Main navigation">
        {items
          .filter((i) => user || !i.requiresAuth)
          .map((item) => (
            <ListItemButton
              key={item.to}
              component={RouterLink}
              to={item.to}
              selected={isActive(item, pathname)}
              onClick={onNavigate}
              sx={{ mb: 0.25 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          ))}
      </List>
    </Box>
  );
}

function AccountMenu() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  if (!user) {
    return (
      <>
        <Button component={RouterLink} to="/login" sx={{ display: { xs: 'none', sm: 'inline-flex' } }}>
          Sign in
        </Button>
        <Button component={RouterLink} to="/register" variant="contained">
          Get started
        </Button>
      </>
    );
  }
  const name = profile?.display_name || user.email || 'Account';
  return (
    <>
      <Tooltip title="Account">
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Open account menu" aria-haspopup="menu">
          <Avatar src={profile?.avatar_url ?? undefined} sx={{ width: 34, height: 34, bgcolor: 'secondary.main', fontSize: 15 }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="subtitle2">{name}</Typography>
          <Typography variant="body2" color="text.secondary">
            {user.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem component={RouterLink} to="/profile" onClick={() => setAnchor(null)}>
          Profile
        </MenuItem>
        <MenuItem component={RouterLink} to="/purchases" onClick={() => setAnchor(null)}>
          Purchases
        </MenuItem>
        <MenuItem component={RouterLink} to="/support" onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <HelpOutlineIcon fontSize="small" />
          </ListItemIcon>
          Help & support
        </MenuItem>
        <DarkModeMenuItem />
        <Divider />
        <MenuItem
          onClick={async () => {
            setAnchor(null);
            await signOut();
            navigate('/');
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { user } = useAuth();
  const area = areaFromPath(pathname);
  const showBottomNav = !desktop && !!user && area === 'learner';
  const bottomValue = learnerBottomNav.findIndex((i) => isActive(i, pathname));

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <AppBar position="fixed" sx={{ zIndex: (t) => t.zIndex.drawer + 1, width: { md: `calc(100% - ${DRAWER_WIDTH}px)` }, ml: { md: `${DRAWER_WIDTH}px` }, pt: 'env(safe-area-inset-top)' }}>
        <Toolbar sx={{ gap: 1 }}>
          {!desktop && (
            <IconButton edge="start" onClick={() => setOpen(true)} aria-label="Open navigation">
              <MenuIcon />
            </IconButton>
          )}
          {!desktop && <Logo to={user ? '/dashboard' : '/'} />}
          {desktop && area !== 'learner' && (
            <Typography variant="subtitle1" color="text.secondary">
              {area === 'provider' ? 'Content Provider Portal' : 'Admin Portal'}
            </Typography>
          )}
          <Box sx={{ flex: 1 }} />
          <AccountMenu />
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}>
        {desktop ? (
          <Drawer variant="permanent" open slotProps={{ paper: { sx: { width: DRAWER_WIDTH, borderRight: 1, borderColor: 'divider' } } }}>
            <DrawerContent />
          </Drawer>
        ) : (
          <Drawer variant="temporary" open={open} onClose={() => setOpen(false)} slotProps={{ paper: { sx: { width: DRAWER_WIDTH, pt: 'env(safe-area-inset-top)' } } }}>
            <DrawerContent onNavigate={() => setOpen(false)} />
          </Drawer>
        )}
      </Box>

      <Box
        component="main"
        id="main"
        tabIndex={-1}
        sx={{ flex: 1, minWidth: 0, pt: 'calc(64px + env(safe-area-inset-top))', pb: showBottomNav ? 'calc(72px + env(safe-area-inset-bottom))' : 4 }}
      >
        <DevModeBanner />
        <Container maxWidth={area === 'learner' ? 'lg' : 'xl'} sx={{ py: { xs: 2, md: 4 } }}>
          {user && area === 'learner' && <TrialBanner />}
          <Suspense fallback={<LoadingState />}>{children ?? <Outlet />}</Suspense>
        </Container>
      </Box>

      {showBottomNav && <InstallBanner />}

      {showBottomNav && (
        <Paper elevation={8} sx={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: (t) => t.zIndex.appBar, pb: 'env(safe-area-inset-bottom)' }}>
          <BottomNavigation showLabels value={bottomValue === -1 ? false : bottomValue} component="nav" aria-label="Quick navigation">
            {learnerBottomNav.map((item) => (
              <BottomNavigationAction key={item.to} label={item.label} icon={item.icon} component={RouterLink} to={item.to} sx={{ minWidth: 0 }} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
}
